
import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {X, AlertCircle, Info, Megaphone, Settings} from 'lucide-react'
import { lumi } from '../lib/lumi'

interface Announcement {
  _id: string
  title: string
  content: string
  category: string
  showPopup: boolean
  startDate?: string
  endDate?: string
  priority?: number
  status: string
  createdAt: string
}

const AnnouncementPopup: React.FC = () => {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchActiveAnnouncement()
  }, [])

  const fetchActiveAnnouncement = async () => {
    try {
      console.log('=== 공지사항 팝업 시작 ===')
      
      const result = await lumi.entities.announcements.list({
        limit: 100
      })

      console.log('전체 공지사항 개수:', result.list?.length || 0)

      if (!result.list || result.list.length === 0) {
        console.log('❌ 공지사항이 없습니다.')
        setLoading(false)
        return
      }

      const now = new Date()

      // 활성 공지사항 필터링
      const activeAnnouncements = result.list.filter((item) => {
        // 상태가 published인지 확인
        if (item.status !== 'published') {
          console.log(`제외: ${item.title} - 상태: ${item.status}`)
          return false
        }

        // 팝업 표시 설정 확인
        if (!item.showPopup) {
          console.log(`제외: ${item.title} - showPopup: false`)
          return false
        }

        // 시작일 확인
        if (item.startDate) {
          const startDate = new Date(item.startDate)
          if (now < startDate) {
            console.log(`제외: ${item.title} - 아직 시작 전`)
            return false
          }
        }

        // 종료일 확인
        if (item.endDate) {
          const endDate = new Date(item.endDate)
          if (now > endDate) {
            console.log(`제외: ${item.title} - 종료됨`)
            return false
          }
        }

        // 하루 동안 숨김 확인
        const hideKey = `hideAnnouncement_${item._id}`
        const hideUntil = localStorage.getItem(hideKey)
        if (hideUntil) {
          const hideDate = new Date(hideUntil)
          if (now < hideDate) {
            console.log(`제외: ${item.title} - 하루 동안 숨김 설정`)
            return false
          } else {
            localStorage.removeItem(hideKey)
          }
        }

        return true
      })

      console.log('활성 공지사항 개수:', activeAnnouncements.length)

      if (activeAnnouncements.length === 0) {
        console.log('표시할 공지사항이 없습니다.')
        setLoading(false)
        return
      }

      // 우선순위가 높은 순으로 정렬
      activeAnnouncements.sort((a, b) => (b.priority || 1) - (a.priority || 1))
      
      const selectedAnnouncement = activeAnnouncements[0]
      setAnnouncement(selectedAnnouncement)
      setIsVisible(true)
      console.log('✅ 공지사항 팝업 표시:', selectedAnnouncement.title)

    } catch (error) {
      console.error('❌ 공지사항 조회 실패:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setIsVisible(false)
  }

  const handleHideForDay = () => {
    if (announcement) {
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      tomorrow.setHours(0, 0, 0, 0)
      
      const hideKey = `hideAnnouncement_${announcement._id}`
      localStorage.setItem(hideKey, tomorrow.toISOString())
      console.log('하루 동안 숨김 설정:', hideKey)
    }
    setIsVisible(false)
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'urgent': 
        return <AlertCircle className="text-red-500" size={24} />
      case 'update': 
        return <Settings className="text-blue-500" size={24} />
      case 'event': 
        return <Megaphone className="text-purple-500" size={24} />
      case 'maintenance': 
        return <Settings className="text-orange-500" size={24} />
      default: 
        return <Info className="text-gray-500" size={24} />
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'urgent': 
        return 'border-red-500 bg-red-50'
      case 'update': 
        return 'border-blue-500 bg-blue-50'
      case 'event': 
        return 'border-purple-500 bg-purple-50'
      case 'maintenance': 
        return 'border-orange-500 bg-orange-50'
      default: 
        return 'border-gray-500 bg-gray-50'
    }
  }

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'urgent': return '긴급'
      case 'update': return '업데이트'
      case 'event': return '이벤트'
      case 'maintenance': return '점검'
      default: return '일반'
    }
  }

  if (loading) {
    console.log('로딩 중...')
    return null
  }

  if (!announcement || !isVisible) {
    console.log('공지사항 없음 또는 숨김 상태')
    return null
  }

  console.log('공지사항 팝업 렌더링:', announcement.title)

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleClose()
          }
        }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className={`bg-white rounded-2xl shadow-2xl max-w-md w-full border-2 ${getCategoryColor(announcement.category)} relative`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* 카테고리 배지 */}
          <div className="absolute -top-3 left-6">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
              announcement.category === 'urgent' ? 'bg-red-500 text-white' :
              announcement.category === 'update' ? 'bg-blue-500 text-white' :
              announcement.category === 'event' ? 'bg-purple-500 text-white' :
              announcement.category === 'maintenance' ? 'bg-orange-500 text-white' :
              'bg-gray-500 text-white'
            }`}>
              {getCategoryLabel(announcement.category)}
            </span>
          </div>

          <div className="p-6 pt-8">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3 flex-1">
                {getCategoryIcon(announcement.category)}
                <h2 className="text-xl font-bold text-gray-900 leading-tight">
                  {announcement.title}
                </h2>
              </div>
              <button
                onClick={handleClose}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0 ml-2"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="mb-6">
              <div 
                className="text-gray-700 leading-relaxed whitespace-pre-line"
                dangerouslySetInnerHTML={{ 
                  __html: announcement.content.replace(/\n/g, '<br>') 
                }}
              />
            </div>

            {/* 날짜 정보 표시 */}
            {(announcement.startDate || announcement.endDate) && (
              <div className="mb-4 text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                {announcement.startDate && (
                  <div>시작: {new Date(announcement.startDate).toLocaleDateString('ko-KR')}</div>
                )}
                {announcement.endDate && (
                  <div>종료: {new Date(announcement.endDate).toLocaleDateString('ko-KR')}</div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleHideForDay}
                className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium"
              >
                하루 동안 보지 않기
              </button>
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                확인
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default AnnouncementPopup
